import React from 'react';
import ArrowIcon from './Icons/ArrowIcon';

/**
 * SelectField component renders a customizable dropdown select field with validation and an optional arrow icon.
 *
 * @param {string} label - The label for the select field, used in the placeholder and error messages.
 * @param {string} name - The name of the select field, used for form registration and validation.
 * @param {Array} options - The options to display in the dropdown. Each option should have an id and a name.
 * @param {function} register - The register function from react-hook-form to manage form validation.
 * @param {object} errors - The validation errors object from react-hook-form.
 * @param {boolean} [disabled=false] - Optional: If true, disables the select field.
 * @param {string} [errorMessage='This field cannot be empty'] - Optional: Custom error message to show when the field is empty.
 *
 * @returns {JSX.Element} Rendered select field with the specified options and label.
 */
const SelectField = ({
  label,
  name,
  options,
  register,
  errors,
  disabled = false,
  errorMessage = 'This field cannot be empty',
}) => {
  return (
    <div className="relative">
      {/* Select dropdown */}
      <select
        {...register(name, { required: errorMessage })} // Registers input with validation rules (required field)
        id={name} // The id for the select field, important for labeling
        name={name} // Name used for form submission
        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-[56px] appearance-none disabled:bg-gray-300 disabled:text-gray-400"
        disabled={disabled} // Disables the select field when the disabled flag is true
      >
        {/* Placeholder option */}
        <option value="">Select {label}</option>

        {/* Map through the options array and render option elements */}
        {options.map(({ name, id }) => (
          <option key={id} value={id}>
            {' '}
            {/* Render option with a unique key and value */}
            {name} {/* Display the name of each option */}
          </option>
        ))}
      </select>

      {/* Arrow down icon for dropdown indication */}
      <div className="absolute right-3 top-4 pointer-events-none">
        <ArrowIcon /> {/* Custom icon for the dropdown indicator */}
      </div>
    </div>
  );
};

export default SelectField;
